/**
 * Attendance Storage Service
 * Uses IndexedDB for local storage. Owlet can receive a credential-free snapshot
 * only after the student separately enables attendance access in the chat UI.
 */

import { AttendanceData } from '@/types/attendance';

const DB_NAME = 'AttendanceDB';
const DB_VERSION = 2; // Bumped version for new credential storage
const STORE_NAME = 'attendance';

// Simple obfuscation for stored credentials (not encryption, but better than plaintext)
function obfuscate(text: string): string {
  return btoa(encodeURIComponent(text).split('').reverse().join(''));
}

function deobfuscate(text: string): string {
  try {
    return decodeURIComponent(atob(text).split('').reverse().join(''));
  } catch {
    return '';
  }
}

class AttendanceStorageService {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB');
        reject(new Error('Failed to open local database'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        }
      };
    });
  }

  private async ensureDb(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init();
    }
    return this.db!;
  }

  async saveAttendance(data: AttendanceData): Promise<void> {
    const db = await this.ensureDb();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.put({
        key: 'attendanceData',
        value: data,
        timestamp: Date.now()
      });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to save attendance data'));
    });
  }

  async getAttendance(): Promise<AttendanceData | null> {
    const db = await this.ensureDb();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get('attendanceData');

      request.onsuccess = () => {
        const result = request.result;
        resolve(result?.value || null);
      };
      request.onerror = () => reject(new Error('Failed to read attendance data'));
    });
  }

  async setConsent(given: boolean): Promise<void> {
    const db = await this.ensureDb();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.put({
        key: 'consent',
        value: given,
        timestamp: Date.now()
      });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to save consent'));
    });
  }

  async getConsent(): Promise<boolean> {
    const db = await this.ensureDb();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get('consent');

      request.onsuccess = () => {
        const result = request.result;
        resolve(result?.value || false);
      };
      request.onerror = () => reject(new Error('Failed to read consent'));
    });
  }

  // Save credentials locally (obfuscated)
  async saveCredentials(username: string, password: string): Promise<void> {
    const db = await this.ensureDb();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.put({
        key: 'credentials',
        username: obfuscate(username),
        password: obfuscate(password),
        timestamp: Date.now()
      });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to save credentials'));
    });
  }

  // Get stored credentials
  async getCredentials(): Promise<{username: string, password: string} | null> {
    const db = await this.ensureDb();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get('credentials');

      request.onsuccess = () => {
        const result = request.result;
        if (result?.username && result?.password) {
          resolve({
            username: deobfuscate(result.username),
            password: deobfuscate(result.password)
          });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(new Error('Failed to read credentials'));
    });
  }

  // Clear stored credentials
  async clearCredentials(): Promise<void> {
    const db = await this.ensureDb();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete('credentials');

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to clear credentials'));
    });
  }

  // Check if credentials are stored
  async hasStoredCredentials(): Promise<boolean> {
    const creds = await this.getCredentials();
    return creds !== null;
  }

  async getLastFetchTimestamp(): Promise<number | null> {
    const db = await this.ensureDb();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get('attendanceData');

      request.onsuccess = () => {
        const result = request.result;
        resolve(result?.timestamp || null);
      };
      request.onerror = () => reject(new Error('Failed to read timestamp'));
    });
  }

  async clearAllData(): Promise<void> {
    const db = await this.ensureDb();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('All attendance data cleared from local storage');
        resolve();
      };
      request.onerror = () => reject(new Error('Failed to clear data'));
    });
  }

  // Utility to check if data is stale (older than 1 hour)
  async isDataStale(): Promise<boolean> {
    const timestamp = await this.getLastFetchTimestamp();
    if (!timestamp) return true;
    
    const oneHour = 60 * 60 * 1000;
    return Date.now() - timestamp > oneHour;
  }
}

// Singleton instance
export const attendanceStorage = new AttendanceStorageService();
